'use client';

import {
  commandLinkUnconfirmed,
  COMMAND_LINK_UNCONFIRMED,
  COMMAND_LINK_UNCONFIRMED_DETAIL,
  hasNoCommandLink,
  noCommandLinkSinceMs,
  NO_COMMAND_LINK,
  NO_COMMAND_LINK_ACTION,
} from '@/lib/companion-command-link';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { usePointerDrag } from '@/hooks/usePointerDrag';
import { clampDockSheetWidth } from './CopDockSheet';
import { useDroneVideoStream } from './useDroneVideoStream';
import {
  formatLensReadout,
  formatPitchReadout,
  formatYawReadout,
  formatZoomReadout,
} from './useCameraGestures';
import type { HlsStreamState } from '@/components/dashboard/HlsVideo';
import {
  batteryLevel,
  batteryPercent,
  exceptionsFor,
  formatAge,
  formatOffsetM,
  homeBearingDeg,
  homeDistanceM,
  linkPercent,
  linkFreshness,
  signalLevel,
  MIN_HEALTHY_SATS,
  type DroneException,
  type Level,
} from '@/lib/drone-telemetry';
import { SINGLE_CAMERA, type DetailsSegment } from './copReducers';
import {
  chooseStream,
  isCameraPopped,
  resolveCameraKey,
  stagePlacementSentence,
  stageTakesBack,
} from './stageCamera';
import type { ManualStageLayout } from '@/lib/manual-layout';
import type { LiveDrone } from '@/hooks/useWebSocket';
import type { AlarmEntry } from '@/components/dashboard/alarmTypes';
import {
  maintenanceTypeLabel,
  type MaintenancePrediction,
} from '@/lib/compliance';
import type { CompanionStatus, Drone } from '@/types/fleet';
import type { Dock } from '@/types/dock';
import type { ActiveMission, CopVideoStream } from './types';
import { STATUS_LABEL, WORKSPACE_SEGMENT } from '@/lib/copy';

/**
 * Level three: the full record for one aircraft.
 *
 * Everything here is deliberately absent from the card and the bar — this is
 * where an operator goes to look something up, not to fly. It is also the only
 * place the COP surfaces the registry columns that have existed for two
 * migrations without a UI: payloads (016) and dock/bay assignment (015).
 *
 * Two segments, split by tempo rather than by topic: LIVE is everything on the
 * 1 Hz ticker, FEED is the polled stream registry. Four tabs failed because they
 * ran on different clocks, so most of the moving data was hidden at any moment;
 * splitting by tempo means the operator never changes segment to watch something
 * change.
 *
 * There were three. A/C was the static registry row, and it went to a row
 * expansion on the fleet table in slice 11 (D53) - nothing on it changes during
 * a sortie, and it is the only part of this panel an operator reads when they
 * are NOT flying. What is left is two segments at the 320 px default, where
 * three was what set the 288 px minimum.
 *
 * The panel holds no camera authority whatsoever — no pan, tilt, zoom,
 * recentre, fullscreen, record or snapshot. Its only video verb is the handoff
 * that opens the video window. One surface commands the camera.
 */

/**
 * The two segments, in the copy module's words (#505 T8).
 *
 * The width argument that shaped these labels is now slack rather than tight: the panel is
 * never narrower than `MIN_DOCK_SHEET_WIDTH` (288 px), which used to leave about 88 px a
 * segment across three, and leaves about 132 px across two.
 */
const SEGMENTS: Array<{ id: DetailsSegment; label: string }> = [
  { id: 'live', label: WORKSPACE_SEGMENT.live },
  { id: 'feed', label: WORKSPACE_SEGMENT.feed },
];

/**
 * The stream statuses that mean "the picture is up". `/api/video/streams`
 * currently only ever answers 'active' (src/api/video.ts normalises every read
 * to it), so that is the value the healthy path must actually match — matching
 * 'live' alone would paint every working feed in the caution colour. 'live' is
 * kept so a backend that starts reporting it needs no frontend change.
 */
const HEALTHY_STREAM_STATUS = new Set(['active', 'live']);

function isHealthy(stream: CopVideoStream): boolean {
  return HEALTHY_STREAM_STATUS.has((stream.status || '').toLowerCase());
}

/**
 * The FEED button's status dot, and the words that carry the same fact for
 * anyone who is not reading colour. The dot is driven by the stream's status,
 * not by the mere existence of a registry row: a row that says the encoder
 * stopped must not read as "your video is up". Green healthy, amber registered
 * but not healthy, red could-not-ask, grey nothing or nothing yet — never
 * optimistically green before the answer lands.
 */
interface FeedHealth {
  dotClass: string;
  /** Appended to the button's visible label to form its accessible name. */
  text: string;
}

/** How many of this aircraft's pictures are dead, and how many are trying
 *  again after having painted at least once. */
interface FeedPlayers {
  dead: number;
  reconnecting: number;
  total: number;
  /** How many of this aircraft's cameras have a player looking at them at all.
   *  Zero means nobody can answer the question the dot exists to answer — see
   *  the `observed` rung in `feedHealthOf`. */
  observed: number;
}

/** "no picture" for a single camera, "1 of 2 cameras: no picture" when there is
 *  more than one and they disagree. */
function cameraCount(affected: number, total: number, what: string): string {
  return total > 1 ? `${affected} of ${total} cameras: ${what}` : what;
}

function feedHealthOf({
  stream,
  loading,
  error,
  players,
}: {
  stream: CopVideoStream | null;
  loading: boolean;
  error: string | null;
  /** What the pictures under this aircraft are doing. The registry can never
   *  know this: a row stays `active` for its whole TTL while the tile in front
   *  of the operator is black, which is exactly how a green dot came to sit
   *  over a dead picture on 2026-09-02. The players' verdict outranks the
   *  registry's because they are the ones looking at the pixels. */
  players?: FeedPlayers;
}): FeedHealth {
  if (error && !stream) return { dotClass: 'bg-status-red-fg', text: 'stream health unreadable' };
  if (stream) {
    const status = (stream.status || 'registered').toLowerCase();
    // A stale answer we could not refresh is reported as such, not as fact.
    if (error) return { dotClass: 'bg-status-red-fg', text: `stream health unreadable, last ${status}` };
    // Dead beats reconnecting beats the registry's own opinion. Naming the
    // count matters on a two-camera airframe: "no picture" over a visibly
    // working wide camera, because the thermal one died, reads as a lie.
    if (players && players.dead > 0) {
      return {
        dotClass: 'bg-status-red-fg',
        text: `stream ${status}, ${cameraCount(players.dead, players.total, 'no picture')}`,
      };
    }
    // The middle rung. hls.js holds `loading` for its whole reconnect budget -
    // ~175 s of `Connecting…` over a last decoded frame - and a green dot for
    // that long is the same contradiction as a green dot over black.
    if (players && players.reconnecting > 0) {
      return {
        dotClass: 'bg-status-amber-fg',
        text: `stream ${status}, ${cameraCount(players.reconnecting, players.total, 'reconnecting')}`,
      };
    }
    // Nobody is looking at the pixels.
    //
    // This dot was built because a registry row stays `active` for its whole
    // TTL while the tile in front of the operator is black (2026-09-02). The
    // player is the only witness to that, and the player now lives in the
    // floating window — so with no window open there is no witness, and
    // falling through to the registry's own opinion would put the green dot
    // back over exactly the picture that incident was about.
    //
    // Grey and said out loud instead. It is the same "honest, never
    // optimistically green" rung the pre-answer state uses, and it gives the
    // operator the reason to open the window.
    if (players && players.observed === 0 && players.total > 0) {
      return {
        dotClass: 'bg-muted-foreground',
        text: `stream ${status}, picture not observed`,
      };
    }
    return {
      dotClass: isHealthy(stream) ? 'bg-status-green-fg' : 'bg-status-amber-fg',
      text: `stream ${status}`,
    };
  }
  if (loading) return { dotClass: 'bg-muted-foreground', text: 'reading stream health' };
  return { dotClass: 'bg-muted-foreground', text: 'no stream registered' };
}

const SOURCE_LABEL: Record<string, string> = {
  remoteid: 'RemoteID broadcast',
  dji_cloud: 'DJI Cloud API',
  mavlink: 'MAVLink bridge',
  mqtt: 'MQTT companion',
  simulator: 'Simulator',
};

type Row = [label: string, value: string, className?: string];

/** D17: numbers get the monospace face, words do not. A reading is numeric when it
 *  opens with a digit or a sign (`+1.5`, `-0.4`, `12 m`, `3 / 4`, `00:12`); a word
 *  (`DJI Cloud API`, `NONE`, `AGL only`) stays in the sans. */
function isNumericReading(value: unknown): boolean {
  return /^[\s+\-−]*\d/.test(String(value ?? ''));
}

function Facts({ rows }: { rows: Array<Row | null> }) {
  return (
    <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1.5 rounded-md border border-divider bg-elevated px-2.5 py-2">
      {rows.filter((r): r is Row => r !== null).map(([label, value, className]) => (
        <div key={label} className="contents">
          <dt className="text-[11px] text-muted-foreground">{label}</dt>
          <dd className={cn('m-0 text-right text-[12px]', isNumericReading(value) && 'font-mono tabular-nums', className)}>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * The panel's conclusion in one line — and, on demand, the list behind it.
 *
 * The verdict is still the point: the operator should not have to read twelve
 * rows, or an absent strip, to infer "nothing is wrong". So the headline is a
 * single statement of whether this aircraft is fit, and it renders first.
 *
 * The itemised list used to belong to DroneStateCard, and this component's
 * `+N MORE` was a pointer to it. Replacing that card with DroneSelectionChip
 * took the list off the map — the chip's pill shows the worst condition and a
 * count, and nothing else — so the count here would have pointed at a surface
 * that no longer exists, leaving conditions two and beyond unreachable from
 * anywhere in the product. The panel is where data lives, so the panel takes
 * the list: `+N MORE` is now the disclosure that reveals it.
 */
function Verdict({ exceptions }: { exceptions: DroneException[] }) {
  const [expanded, setExpanded] = useState(false);
  const listId = useId();
  const worst = exceptions[0];
  const rest = exceptions.slice(1);

  if (!worst) {
    return (
      <div className="flex shrink-0 items-center gap-2 border-b border-divider border-l-[3px] border-l-status-green bg-status-green/[0.06] px-3 py-1.5">
        <span className="text-[11.5px] font-semibold tracking-[0.08em] text-status-green-fg">
          NOMINAL
        </span>
        <span className="ml-auto text-[10px] tracking-[0.06em] text-muted-foreground">
          NO EXCEPTIONS
        </span>
      </div>
    );
  }

  const critical = worst.severity === 'critical';
  const tone = critical ? 'text-status-red-fg' : 'text-status-amber-fg';
  const surface = cn(
    'border-l-[3px]',
    critical ? 'border-l-status-red bg-status-red/[0.07]' : 'border-l-status-amber bg-status-amber/[0.07]',
  );

  const headline = (
    <>
      <span aria-hidden className={tone}>{critical ? '▼' : '▲'}</span>
      <span
        className={cn(
          'min-w-0 text-[11.5px] font-semibold tracking-[0.06em]',
          expanded ? 'break-words text-left' : 'truncate',
          tone,
        )}
      >
        {worst.label}
      </span>
    </>
  );

  // Nothing to reveal, so this is not a control. A button that does nothing when
  // pressed reads as broken.
  if (rest.length === 0) {
    return (
      <div className={cn('flex shrink-0 items-center gap-2 border-b border-divider px-3 py-1.5', surface)}>
        {headline}
      </div>
    );
  }

  return (
    <div className={cn('shrink-0 border-b border-divider', surface)}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-controls={expanded ? listId : undefined}
        aria-label={`${worst.label}, and ${rest.length} further condition${rest.length === 1 ? '' : 's'}`}
        className="flex w-full items-center gap-2 px-3 py-1.5 text-left outline-none hover:bg-foreground/[0.04] focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring"
      >
        {headline}
        {/* A word, not a chevron: ▾ beside the ▼/▲ severity glyphs reads as a
            third severity. */}
        <span aria-hidden className="ml-auto shrink-0 text-[10px] tracking-[0.06em] text-muted-foreground">
          {expanded ? 'HIDE' : `+${rest.length} MORE`}
        </span>
      </button>

      {/* Unmounted rather than hidden with a class: collapsed means the rows are
          not in the panel at all, so nothing can reach them by find-in-page or a
          stray tab stop, and the collapse is verifiable without a stylesheet. */}
      {expanded && (
        <ul id={listId} className="flex flex-col border-t border-divider">
          {rest.map((e, i) => (
            <li
              key={`${e.kind}-${i}`}
              className={cn(
                'flex items-center gap-2 px-3 py-1.5 text-[10.5px] tracking-[0.05em]',
                i > 0 && 'border-t border-divider',
                e.severity === 'critical' ? 'text-status-red-fg' : 'text-status-amber-fg',
              )}
            >
              <span aria-hidden>{e.severity === 'critical' ? '▼' : '▲'}</span>
              <span className="min-w-0 break-words">{e.label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * The three facts that never scroll and never change with the segment.
 *
 * This grid used to hold four, chosen by exclusion against DroneStateCard: the
 * card owned altitude, battery, ground speed, link percent and satellite count,
 * so none of them appeared here. That split was right while the card existed.
 * It does not survive the card's replacement.
 *
 * DroneSelectionChip keeps only what has to be read at the marker without a
 * click — callsign, battery, altitude — because anything more re-grows the box
 * that was covering the map. The numbers it dropped land here: SPEED, because
 * it pairs with heading and heading is already drawn on the map as the marker's
 * own rotation, and SATELLITES, because this grid already owns fix type and
 * HDOP so the count sits beside the two numbers that qualify it. That split
 * stands, and the chip is not touched by this PR (D48).
 *
 * It then held six, and three of those were the panel repeating itself (D49).
 * LINK and MISSION each had a full Group a few hundred pixels below in the same
 * scroll, and the Group is the fuller answer in both cases — it carries packet
 * loss, freshness and the reason a percent is absent for an entire vendor, or
 * the waypoint list. At 560 px drawing both was untidy. At the 320 px default
 * this panel now opens to, the head is the whole budget. HOME was not repeated
 * anywhere, so it moved rather than being cut: distance and bearing to the dock
 * are position facts, and Position is where latitude, altitude and heading
 * already are (D50).
 *
 * What is left is still paired by question: is it working and how fast (MODE,
 * GND SPEED), can it trust its fix (GPS). GPS spans both columns because fix
 * class and HDOP make the longest sub-line the grid carries, and it truncates
 * in a third of 320 px (D51).
 */
function FactGrid({ drone }: { drone: LiveDrone }) {
  const hdop = drone.gps_hdop;

  // Returns null rather than 0 when the source broadcasts no count — rendered
  // as an em dash, like an unreported battery. "0 sats" and "not reported" mean
  // very different things to an operator, and only one of them aborts a launch.
  const sats =
    drone.telemetry_source !== 'remoteid' && typeof drone.satellites === 'number'
      ? drone.satellites
      : null;

  // The fix type and HDOP qualify the count, so they ride as its sub-line
  // rather than competing with it for the tile's primary slot.
  const gpsDetail: string[] = [];
  if (drone.gps_fix_type) gpsDetail.push(drone.gps_fix_type.replace(/_/g, ' '));
  if (typeof hdop === 'number') gpsDetail.push(`hdop ${hdop.toFixed(2)}`);

  return (
    <div className="grid shrink-0 grid-cols-2 border-b border-divider">
      <Fact
        label="Mode"
        value={drone.flight_mode ?? '—'}
        valueClass={drone.flight_mode ? undefined : 'text-muted-foreground'}
        // Armed is the difference between an aircraft that can move and one that
        // cannot; absent means the source does not report it, not "disarmed".
        sub={drone.armed === undefined ? 'armed unknown' : drone.armed ? 'ARMED' : 'disarmed'}
        subClass={drone.armed ? 'text-status-amber-fg' : undefined}
      />
      <Fact
        label="Gnd speed"
        value={`${(drone.speed ?? 0).toFixed(1)} m/s`}
        // Heading is already drawn on the map as the marker's rotation, so it
        // sits as the sub-line here rather than claiming a tile of its own.
        sub={`hdg ${String(Math.round(drone.heading ?? 0)).padStart(3, '0')}°`}
      />
      <Fact
        label="GPS"
        // The full width of the grid, not a third of it: `RTK FIXED · hdop 0.70`
        // is the longest sub-line here and the tile truncates (D51).
        className="col-span-2"
        value={sats === null ? '—' : `${sats} sats`}
        valueClass={
          sats === null
            ? 'text-muted-foreground'
            : LEVEL_TEXT[sats < MIN_HEALTHY_SATS ? 'warn' : 'ok']
        }
        sub={gpsDetail.length > 0 ? gpsDetail.join(' · ') : 'not broadcast'}
      />
    </div>
  );
}

function Fact({
  label,
  value,
  sub,
  valueClass,
  subClass,
  className,
}: {
  label: string;
  value: string;
  sub: string;
  valueClass?: string;
  subClass?: string;
  /** Grid placement only — `col-span-2` for the tile that takes a whole row. */
  className?: string;
}) {
  return (
    <div className={cn('border-r border-divider px-2.5 py-2 last:border-r-0 [&:nth-child(n+3)]:border-t [&:nth-child(n+3)]:border-divider', className)}>
      <span className="block text-[9px] uppercase tracking-[0.1em] text-muted-foreground">{label}</span>
      <span className={cn('mt-0.5 block truncate text-[15px] leading-tight', isNumericReading(value) && 'font-mono tabular-nums', valueClass)}>
        {value}
      </span>
      <span className={cn('block truncate text-[9.5px] text-muted-foreground', subClass)}>{sub}</span>
    </div>
  );
}

/**
 * Companion-app liveness (T4).
 *
 * Its whole reason to exist is one distinction nothing else in the panel can
 * make: an aircraft that has gone quiet is either powered off, or still there
 * with our app dead on the RC — and those need opposite responses from the
 * operator, a search party or a restart. Every other reading on this panel
 * describes the aircraft; this one describes OUR software on the RC, which is
 * why it cannot be folded into the fact grid beside Mode and Link.
 *
 * A dedicated row rather than a seventh Fact: a Fact is a label over a
 * measurement, and this is a three-state verdict carrying an optional detail
 * line. It also has to read as words — an odd tile would break the grid's
 * deliberate pairing, two questions per row.
 *
 * Rendered only when this drone has a companion at all, so the MAVLink and
 * RemoteID aircraft grow no row rather than a permanently unsatisfiable one.
 * Nothing here is inferred: an app that reports neither its version nor its
 * MSDK state gets an em dash, never a guess and never "unknown".
 */
function CompanionRow({ companion, now }: { companion: CompanionStatus; now: number }) {
  const online = companion.is_online;
  // "App alive but nothing bound" is the whole point of the row, so it reads as
  // its own state rather than collapsing into a healthy green.
  const bound = companion.aircraft_bound === true;
  // #818: the app is alive and may even be streaming telemetry, but nothing sent
  // from here reaches the aircraft. It outranks every "online" state, because it
  // is the one that makes LAND do nothing. See `hasNoCommandLink` for the two
  // facts that say so.
  const noCommandLink = hasNoCommandLink(companion, now);
  // #818 NEW-6: amber, not green - a command was lost and nothing has proved the link since.
  const unconfirmed = commandLinkUnconfirmed(companion, now);
  const tone = !online || noCommandLink
    ? 'text-status-red-fg'
    : unconfirmed
      ? 'text-status-amber-fg'
      : bound
        ? 'text-status-green-fg'
        : 'text-status-amber-fg';
  const label = !online
    ? 'COMPANION OFFLINE'
    : noCommandLink
      ? NO_COMMAND_LINK
      : unconfirmed
        ? COMMAND_LINK_UNCONFIRMED
        : bound
          ? 'COMPANION ONLINE'
          : 'COMPANION — NO AIRCRAFT';

  const detail: string[] = [];
  const downSince = noCommandLinkSinceMs(companion, now);
  if (noCommandLink) {
    // What to DO, not the app's build: under time pressure the version is noise.
    detail.push(NO_COMMAND_LINK_ACTION);
    if (downSince !== null) detail.push(formatAge(new Date(downSince).toISOString(), now));
  } else if (unconfirmed) {
    detail.push(COMMAND_LINK_UNCONFIRMED_DETAIL);
  } else {
    if (companion.app_version) detail.push(`v${companion.app_version}`);
    if (companion.msdk_state) detail.push(`MSDK ${companion.msdk_state}`);
  }

  return (
    <div className="flex shrink-0 items-baseline gap-2 border-b border-divider px-3 py-1.5">
      {/* role="status" on the LABEL only: the row changing to NO COMMAND LINK is announced, and
          the age beside it - which ticks every second - is not re-read aloud every second. */}
      <span role="status" className={cn('shrink-0 text-[10px] uppercase tracking-[0.08em]', tone)}>{label}</span>
      {/* Wraps rather than truncates, as the bar's notice does: the ACTION is what truncation cut. */}
      <span title={detail.join(' · ') || undefined} className="ml-auto min-w-0 text-right text-[10px] text-muted-foreground">
        {detail.length > 0 ? detail.join(' · ') : '—'}
      </span>
    </div>
  );
}

/**
 * One group of the record, open or closed.
 *
 * Reset on every aircraft: an operator selecting a new drone gets the same
 * panel every time rather than inheriting whatever they had opened on the last
 * one. That is the remount key on LiveSegment, not state in here.
 *
 * Four of the six start closed, and the summary is what makes that affordable —
 * a closed group still answers for its contents. Position and Power start open,
 * because they are the two an operator opens on every single selection and D11
 * says a control that only ever goes one way should not ask (D52).
 *
 * There is no shared disclosure primitive in components/ui to reuse, and this
 * is the only place in the COP that needs one, so it stays local rather than
 * becoming a speculative abstraction.
 */
function Group({
  label,
  summary,
  summaryClass,
  defaultOpen = false,
  children,
}: {
  label: string;
  summary: string;
  summaryClass?: string;
  /** Seeds the initial state only. The remount per aircraft is what returns a
   *  group to it, so this stays a default and never becomes a controlled prop. */
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const bodyId = useId();

  return (
    <section className="mb-2 overflow-hidden rounded-md border border-divider bg-elevated">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        // Only while open, because the body is not in the DOM otherwise. A
        // dangling IDREF is worse than omitting an attribute ARIA only
        // recommends - the same reasoning the segment tabs below carry, and
        // four of six groups are closed on mount (D61).
        aria-controls={open ? bodyId : undefined}
        className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left hover:bg-accent/40"
      >
        <span className="text-[9.5px] uppercase tracking-[0.1em] text-muted-foreground">{label}</span>
        {/* A summary answers FOR contents that are not on screen, so it has no
            job once they are. Rendering it either way drew MSL 163 m directly
            above "Altitude MSL · 163 m", and 22.4 V above "Voltage · 22.4 V",
            on every selection - which D52 made unconditional the moment it
            opened these two by default (D60). */}
        {!open && (
          <span className={cn('ml-auto text-[11px]', isNumericReading(summary) && 'font-mono tabular-nums', summaryClass ?? 'text-muted-foreground')}>
            {summary}
          </span>
        )}
        {/* Takes the summary's `ml-auto` when there is no summary to hold the
            right edge, so the caret does not slide back against the label. */}
        <span aria-hidden className={cn('w-2 text-center text-[9px] text-muted-foreground', open && 'ml-auto')}>
          {open ? '▴' : '▾'}
        </span>
      </button>
      {open && (
        <div id={bodyId} className="border-t border-divider px-2 pb-2 pt-1.5">
          {children}
        </div>
      )}
    </section>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className="mb-3.5">
      <span className="mb-1.5 block text-[9.5px] uppercase tracking-[0.1em] text-muted-foreground">
        {label}
      </span>
      {children}
    </section>
  );
}

export interface DroneWorkspaceProps {
  drone: LiveDrone;
  /** Companion-app liveness, when this aircraft is flown through the RC app.
   *  Null for every other aircraft, which renders no companion row at all. */
  companion?: CompanionStatus | null;
  registryRow?: Drone | null;
  homeDock?: Dock | null;
  mission?: ActiveMission | null;
  /** Org alarm list; only this aircraft's unresolved entries reach the verdict. */
  alerts?: AlarmEntry[];
  /** Prediction rows for this aircraft, already loaded by useCopContextData. */
  maintenance?: MaintenancePrediction[];
  /** Omitted for a viewer, whose role cannot acknowledge or resolve. */
  onAcknowledgeAlert?: (id: string) => void;
  onResolveAlert?: (id: string) => void;
  missionWaypointIndex?: number | null;
  /** The mission set could not be read. Distinguishes "this aircraft has no
   *  mission" from "we do not know", which must never be shown as the same. */
  missionsUnavailable?: boolean;
  segment: DetailsSegment;
  onSegmentChange: (segment: DetailsSegment) => void;
  /** Open this camera's floating window over the map — or raise it if it is
   *  already open. A PLACEMENT verb: where the picture is drawn, not where the
   *  camera is pointed. This panel draws no picture itself, so it is the only
   *  way from here to one. */
  onPopOutVideo: (cameraId?: string) => void;
  /** Close this camera's window so the STAGE takes the picture back. Offered
   *  only when there is a stage waiting for it; there is no inline home here
   *  any more. */
  onDockVideo: (cameraId?: string) => void;
  /** Camera ids currently in a floating window. */
  poppedCameras: string[];
  /** Where the stage is drawing this aircraft's picture, if it is: a manual
   *  session's layout, or `split` for the regular-mode picture beside the map.
   *  Decides which of the two placements this panel reports for each camera,
   *  and therefore whether closing its window hands it to the stage. Which
   *  camera the stage chose follows from `chooseStream` over the same facts. */
  stagePlacement?: ManualStageLayout | null;
  /**
   * What each camera's PLAYER is doing, keyed by stream id, from whichever
   * surface is drawing it — today the floating windows.
   *
   * Passed in rather than collected here since the picture left this panel. The
   * health dot on the FEED button is the answer to "is my video up?" read from
   * the LIVE tab without spending a click, and the registry row alone cannot
   * give it: a stream can be registered, current, and painting nothing. So the
   * verdict has to come from wherever the player actually is.
   *
   * `painted` separates a first connect from a RECONNECT over a frozen picture;
   * only the second is worth the operator's attention. Absent (no window open
   * for this aircraft) and the dot falls back to what the registry can prove,
   * which is honest rather than optimistic.
   */
  playerStates?: Record<string, { state: HlsStreamState; painted: boolean }>;
  onClose: () => void;
  /** Drawer peeked shut below `lg`. Owned by the selection reducer, because
   *  every route that opens the panel has to be able to un-collapse it. */
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  width: number;
  onWidthChange: (width: number) => void;
  now?: number;
}

export function DroneWorkspace({
  drone,
  companion,
  registryRow,
  homeDock,
  mission,
  alerts = [],
  maintenance = [],
  onAcknowledgeAlert,
  onResolveAlert,
  missionWaypointIndex = null,
  missionsUnavailable = false,
  segment,
  onSegmentChange,
  onPopOutVideo,
  onDockVideo,
  poppedCameras = [],
  stagePlacement = null,
  playerStates = {},
  onClose,
  collapsed = false,
  onToggleCollapsed,
  width,
  onWidthChange,
  now: nowOverride,
}: DroneWorkspaceProps) {
  const callsign = drone.callsign?.trim() || drone.drone_id.slice(-6).toUpperCase();
  const baseId = useId();

  // Polled here rather than inside FEED because the health dot lives on the
  // segment button: the answer to "is my video up?" must be visible from LIVE
  // without spending a click to find out.
  const feed = useDroneVideoStream(drone.drone_id);
  // A camera the registry no longer offers is filtered out HERE rather than
  // evicted by an effect: reading is the only place staleness can do harm, and
  // an effect that calls setState just to drop a key costs a render pass to say
  // something the read already knows.
  const liveIds = new Set(feed.streams.map((s) => s.id));
  const reported = Object.entries(playerStates)
    .filter(([id]) => liveIds.has(id))
    .map(([, p]) => p);
  const feedHealth = feedHealthOf({
    ...feed,
    players: {
      dead: reported.filter((p) => p.state === 'error').length,
      reconnecting: reported.filter((p) => p.state === 'loading' && p.painted).length,
      total: Math.max(feed.streams.length, reported.length),
      observed: reported.length,
    },
  });

  // Age and freshness must keep advancing even when no telemetry arrives — a
  // drone going quiet is exactly when the operator needs the clock to move.
  const [tick, setTick] = useState(() => Date.now());
  useEffect(() => {
    if (nowOverride !== undefined) return;
    const id = window.setInterval(() => setTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [nowOverride]);
  const now = nowOverride ?? tick;

  // Same pointer-drag resize as the dock sheet — both right-hand panels now run
  // the one shared hook, so they cannot drift apart in feel again.
  const startWidth = useRef(width);
  const { onPointerDown: onResizePointerDown } = usePointerDrag({
    cursor: 'ew-resize',
    onDragStart: () => {
      startWidth.current = width;
    },
    // The panel is anchored right, so dragging left widens it.
    onDrag: ({ dx }) =>
      onWidthChange(clampDockSheetWidth(startWidth.current - dx, window.innerWidth)),
  });

  // Roving tabindex: exactly one segment button is in the tab order, and the
  // arrow keys move between them. A console without a usable mouse is a real
  // operating condition, not an accessibility afterthought.
  const segmentRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const handleSegmentKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const current = SEGMENTS.findIndex((s) => s.id === segment);
      const last = SEGMENTS.length - 1;
      let next: number;
      switch (e.key) {
        case 'ArrowRight':
          next = current === last ? 0 : current + 1;
          break;
        case 'ArrowLeft':
          next = current <= 0 ? last : current - 1;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = last;
          break;
        default:
          return;
      }
      e.preventDefault();
      onSegmentChange(SEGMENTS[next].id);
      segmentRefs.current[next]?.focus();
    },
    [segment, onSegmentChange],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // A confirm dialog opened over the workspace owns Escape; without this,
      // cancelling a command also closes the panel behind it.
      if (document.querySelector('[role="alertdialog"], [role="dialog"]')) return;
      // Same for a text field: Escape there means "dismiss what I am typing"
      // (the header drone search), not "close the panel I am reading".
      const target = e.target as HTMLElement | null;
      if (
        target
        && (target.isContentEditable
          || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))
      ) {
        return;
      }
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Collapsed is a mobile-only idea: below `lg` this is a drawer over the map,
  // and the operator has to be able to peek underneath it without losing the
  // panel. CopDockSheet and CopMissionsPanel both carry exactly this, and the
  // dock sheet collapses itself before centring the map on an aircraft.
  //
  // It is a PROP, not local state. As local state the panel could be open and
  // collapsed while the reducer believed it was simply open, so OPEN_DETAILS —
  // what the chip's exception pill and the `D` hotkey both dispatch — changed
  // nothing the operator could see. The reducer owns it now, and un-collapses
  // on every action that asks for the panel.

  return (
    <aside
      /*
        A side panel on lg, a bottom drawer below it — the same shape
        CopDockSheet, CopMissionsPanel, CopMissionCompose and CopMissionEdit all
        take in this container, whose own comment says "mobile keeps each
        sheet's own fixed-bottom drawer behavior".

        This panel was the one sheet of the five that stayed `hidden lg:flex`,
        and that was survivable only while DroneStateCard carried link, ground
        speed, satellites, companion liveness and the itemised conditions on the
        map at every width. DroneSelectionChip dropped all five into here, so the
        gate stopped hiding a convenience and started hiding the readings
        themselves: under 1024px `OPEN_DETAILS` flipped state and rendered
        nothing, which left the chip's exception pill, the command bar's
        ⊞ DETAILS key and its `D` hotkey all firing into a panel that does not
        exist. Three dead controls, and the aircraft's link quality, satellite
        count and companion state unreachable from anywhere in the product on a
        tablet or an RC screen — the short-landscape viewports DroneCommandBar
        already sizes its dialogs for.

        Width comes from `--dock-sheet-width` rather than an inline style so it
        applies on lg only; the drawer is full-bleed below that.

        `bottom-[76px]`, not `bottom-0`. DroneCommandBar is the last in-flow
        element of the stage column at `h-[60px]` (DroneCommandBar.tsx), and the
        COP container's `p-4` leaves 16px under it — so the flight keys occupy
        exactly the bottom 16-76px of the viewport. A `bottom-0` drawer covered
        the lower half of every one of them, ABORT, RTH and LAND included, and
        because the drawer sits at `z-40` over an unpositioned bar, a press
        there hit the drawer's grab handle instead of commanding the aircraft.
        Collapsing did not help: the ~47px handle is itself what covered them.
        This panel only ever renders for a selected drone, which is the same
        condition that renders the command bar, so the offset is never dead
        space.
      */
      className={`
        flex min-h-0 flex-col overflow-clip
        fixed bottom-[76px] left-0 right-0 z-40 max-w-[100vw]
        rounded-t-xl border-t border-subtle bg-popover/90 shadow-sheet backdrop-blur-sm
        lg:relative lg:bottom-auto lg:left-auto lg:right-auto lg:z-auto lg:h-auto
        lg:w-[var(--dock-sheet-width)] lg:shrink-0
        lg:rounded-none lg:border-0 lg:bg-card lg:shadow-none lg:backdrop-blur-none
      `}
      aria-label={`${callsign} details`}
    >
      <button
        type="button"
        onPointerDown={onResizePointerDown}
        aria-label="Resize details panel"
        /* Dragging the left edge only means anything beside the map. */
        /* `focus-visible`, because this was a focusable button with no focus
           style at all (WCAG 2.4.7) - and the panel floating over a live map is
           what made it impossible for a keyboard operator to tell they were on
           it rather than on the map behind. */
        className="absolute left-0 top-0 z-10 hidden h-full w-1.5 cursor-ew-resize bg-transparent hover:bg-status-blue/30 focus:outline-none focus-visible:bg-primary lg:block"
      />

      {/* `bg-card`, not the aside's `bg-popover/90`: this is the one control that
          exists only on the drawer, and at 90% over a bright basemap its label
          lands at ~3.7:1 — the same arithmetic that made the body opaque two
          elements below. */}
      <button
        type="button"
        className="lg:hidden flex flex-col items-center justify-center gap-1.5 py-2.5 w-full touch-manipulation border-b divider bg-card"
        onClick={onToggleCollapsed}
        aria-expanded={!collapsed}
        aria-label={collapsed ? 'Expand details panel' : 'Collapse details panel'}
      >
        <div className="w-10 h-1 rounded-full bg-muted-foreground/70" aria-hidden />
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Details</span>
      </button>

      {/*
        `bg-card` rather than inheriting the drawer's `bg-popover/90`: this panel
        is a dense grid of 9px sub-lines (`hdop 1.20`, `not reported`, `MSDK
        <state>`), and at 90% the basemap shows through enough to drop
        `--muted-foreground` to about 3.8:1 in the dark theme over the bright
        basemap. The sheets this drawer copies carry prose, not a fact grid.

        `overflow-y-auto` because the block above the tabpanel does not shrink —
        header, verdict, three facts, companion row and the tab strip, and an
        expanded "+N MORE" adds to it. Slice 11 halved the grid's share of that
        (six tiles to three) and D52 spent some of it again below the strip, by
        opening two groups inside the tabpanel. On a short landscape
        screen that pushed the tab strip past the drawer's cap, and the aside's
        own `overflow-hidden` clipped it with no way to scroll to it.
      */}
      <div
        className={cn(
          'flex min-h-0 flex-1 flex-col overflow-y-auto bg-card',
          // `100dvh-11rem` alongside the 70vh cap: the aside also spends 76px of
          // bottom offset (clearing the command bar) and ~47px of grab handle,
          // which `70vh` does not know about. On an RC-class landscape viewport
          // — the ~430-480px-tall screens the comment above names — 70vh was the
          // whole drawer, the ~300px fixed block ate it, and the tab panel, the
          // only flexible child, floored at its own padding: a visible LIVE /
          // FEED / A-C strip with nothing under it. Now the body scrolls, which
          // is the stated intent.
          'max-h-[min(70vh,calc(100dvh-11rem))] lg:max-h-full lg:overflow-clip lg:bg-transparent',
          collapsed && 'hidden lg:flex',
        )}
      >
      <header className="flex shrink-0 items-center gap-2 border-b border-divider px-3 py-2.5">
        <span className="callsign text-[15px] text-foreground">
          {callsign}
        </span>
        <span className="font-mono tabular-nums text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
          {formatAge(drone.last_seen_at, now)}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close details"
          // 24px, as the chip's own deselect is. On a drawer this is the only
          // touch dismissal there is — Escape needs a keyboard.
          className="ml-auto flex size-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <span aria-hidden className="text-sm leading-none">✕</span>
        </button>
      </header>

      {/* Layers 0 and 1 sit outside the scrolling tabpanel on purpose: the
          verdict, the three facts and companion liveness stay put whichever
          segment is open and however far the operator has scrolled inside it.
          Companion liveness belongs with them rather than inside the "Link and
          source" group because it is read at the moment an aircraft goes silent
          — putting it behind a disclosure would leave it further away than it
          was on the card this panel replaced.

          That holds against the TABPANEL's scroll at every width, and against
          the drawer's own scroll only on `lg`, where the wrapper is
          `overflow-clip`. Below `lg` the wrapper scrolls too, so on a short
          screen these can leave the top of the drawer. That is the deliberate
          side of the trade: the alternative — capping the drawer and letting
          only the tabpanel scroll — put the ~300px fixed block over the cap on
          a short landscape screen and clipped the TAB STRIP with no way to
          reach it. Scrolled-away beats unreachable. */}
      {/* Keyed on the aircraft: this panel is reused when the operator selects a
          different drone, and an expansion opened for one aircraft must not
          carry over to the next one's conditions. */}
      <Verdict key={drone.drone_id} exceptions={exceptionsFor(drone, alerts, now, companion)} />

      <FactGrid drone={drone} />

      {companion && <CompanionRow companion={companion}  now={now} />}

      <div
        role="tablist"
        aria-label="Drone details"
        onKeyDown={handleSegmentKeyDown}
        className="flex shrink-0 border-b border-divider"
      >
        {SEGMENTS.map((s, i) => (
          <button
            key={s.id}
            ref={(el) => {
              segmentRefs.current[i] = el;
            }}
            type="button"
            role="tab"
            id={`${baseId}-tab-${s.id}`}
            // Only the open segment has a panel in the DOM. Pointing the other
            // one at an id that does not exist is a dangling IDREF, which is
            // worse than omitting an attribute ARIA only recommends.
            aria-controls={s.id === segment ? `${baseId}-panel-${segment}` : undefined}
            aria-selected={s.id === segment}
            tabIndex={s.id === segment ? 0 : -1}
            onClick={() => onSegmentChange(s.id)}
            className={cn(
              'flex flex-1 items-center justify-center gap-1.5 border-b-2 py-2 text-center text-[10px] tracking-[0.1em]',
              s.id === segment
                ? 'border-status-blue text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {s.id === 'feed' && (
              <span
                aria-hidden
                className={cn('size-[5px] shrink-0 rounded-full', feedHealth.dotClass)}
              />
            )}
            {s.label}
            {/* A comma, not an em dash: this is read aloud, and the dash was the one
                piece of punctuation a screen reader had to make sense of here (#505 T8). */}
            {s.id === 'feed' && <span className="sr-only">, {feedHealth.text}</span>}
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        id={`${baseId}-panel-${segment}`}
        aria-labelledby={`${baseId}-tab-${segment}`}
        tabIndex={0}
        className="min-h-0 flex-1 overflow-y-auto p-3"
      >
        {segment === 'live' && (
          <LiveSegment
            // Remount per aircraft so the groups return to their default state:
            // selecting a new drone must give the same panel every time rather
            // than inheriting whatever was open on the last one. Since D52 that
            // default is Position and Power open, not all six closed.
            key={drone.drone_id}
            drone={drone}
            homeDock={homeDock}
            mission={mission}
            waypointIndex={missionWaypointIndex}
            missionsUnavailable={missionsUnavailable}
            alerts={alerts}
            maintenance={maintenance}
            onAcknowledgeAlert={onAcknowledgeAlert}
            onResolveAlert={onResolveAlert}
            now={now}
          />
        )}
        {segment === 'feed' && (
          <FeedSegment
            drone={drone}
            registryRow={registryRow}
            streams={feed.streams}
            poppedCameras={poppedCameras}
            stagePlacement={stagePlacement}
            loading={feed.loading}
            error={feed.error}
            onRetry={feed.refetch}
            onPopOutVideo={onPopOutVideo}
            onDockVideo={onDockVideo}
            now={now}
          />
        )}
      </div>
      </div>
    </aside>
  );
}

/** Same three levels the selection chip paints with, so a battery that is amber
 *  on one surface is never green on the other. */
const LEVEL_TEXT: Record<Level, string> = {
  ok: 'text-status-green-fg',
  warn: 'text-status-amber-fg',
  critical: 'text-status-red-fg',
};

/**
 * The band a reported link percentage falls in.
 *
 * This was the LINK fact's sub-line until D49 cut that tile, and it is the one
 * thing on it the "Link and source" group did not already carry. It is not
 * decoration: the thresholds live in `signalLevel`, and an operator reading
 * "58 %" has no way to know that is the marginal band without being told. The
 * colour beside it is the redundant second carrier, not the reading — the same
 * argument DroneSelectionChip makes at length for printing its state word.
 *
 * So it moved onto the group's Signal row rather than being deleted with the
 * tile, in the shape `linkSummary` already uses one line above it ("0.0 % loss"
 * — a number, then the word that says what the number is).
 */
const SIGNAL_WORD: Record<Level, string> = {
  ok: 'good',
  warn: 'marginal',
  critical: 'poor',
};

/** live → green, stale → amber, lost → grey. Silence must look like silence. */
const FRESHNESS_CLASS: Record<string, string> = {
  live: 'text-status-green-fg',
  stale: 'text-status-amber-fg',
  lost: 'text-muted-foreground',
};

/**
 * The full live record, in four groups that are closed until asked for.
 *
 * The flat twelve-row list this replaces gave a longitude the same visual
 * weight as a critical battery, and made the panel longer the more it knew.
 * Each group header carries a summary chosen so that a closed group still
 * informs — and chosen not to repeat the state card: MSL rather than AGL,
 * volts rather than percent, packet loss rather than the link percent the card
 * already shows. Colour appears on a summary only when that group holds
 * something out of limits, so a quiet panel means a healthy aircraft.
 *
 * The rows inside are the complete record, including the four numbers the card
 * owns. That is the point of a record — but it is now behind a click, so it no
 * longer competes with the facts an operator reads first.
 */
function LiveSegment({
  drone,
  homeDock,
  mission,
  waypointIndex,
  missionsUnavailable,
  alerts,
  maintenance,
  onAcknowledgeAlert,
  onResolveAlert,
  now,
}: {
  drone: LiveDrone;
  /** Only for the home distance and bearing inside Position, which is where
   *  they moved from the fact grid (D50). */
  homeDock?: Dock | null;
  mission?: ActiveMission | null;
  waypointIndex: number | null;
  missionsUnavailable?: boolean;
  alerts: AlarmEntry[];
  maintenance: MaintenancePrediction[];
  onAcknowledgeAlert?: (id: string) => void;
  onResolveAlert?: (id: string) => void;
  now: number;
}) {
  const battery = batteryPercent(drone);
  const link = linkPercent(drone);
  const freshness = linkFreshness(drone.last_seen_at, now);
  const waypoints = mission?.waypoints ?? [];
  const homeM = homeDistanceM(drone, homeDock ?? null);
  const homeBearing = homeBearingDeg(drone, homeDock ?? null);

  const batteryClass = battery === null ? undefined : LEVEL_TEXT[batteryLevel(battery)];
  const linkClass = link.percent === null ? undefined : LEVEL_TEXT[signalLevel(link.percent)];
  // A stale or lost feed is a fault of the link, so it colours that summary
  // even when the signal number itself is unremarkable.
  const linkSummaryClass = freshness === 'live' ? linkClass : FRESHNESS_CLASS[freshness];

  return (
    <>
      <Group
        label="Position"
        defaultOpen
        summary={drone.altitude_msl == null ? 'AGL only' : `MSL ${Math.round(drone.altitude_msl)} m`}
      >
        <Facts
          rows={[
            // Guarded the way `altitude_msl` below is, and for the same reason
            // one line further down: these are typed non-nullable and
            // `homeDistanceM` does not trust that either. Until D52 they only
            // ran after a click; they now run on every single selection, and
            // there is no error boundary anywhere under `(dashboard)` - a
            // throw here white-screens the whole COP, not just this panel (D63).
            typeof drone.latitude !== 'number'
              ? ['Latitude', 'not reported', 'text-muted-foreground']
              : ['Latitude', `${drone.latitude.toFixed(5)}°`],
            typeof drone.longitude !== 'number'
              ? ['Longitude', 'not reported', 'text-muted-foreground']
              : ['Longitude', `${drone.longitude.toFixed(5)}°`],
            ['Altitude AGL', `${Math.round(drone.altitude ?? 0)} m`],
            // `== null`, not `=== undefined`: the adapter now sends an explicit
            // null for anything the aircraft did not report, and
            // `null === undefined` is false - an undefined-only guard fell
            // through to the value branch and called null.toFixed(), which
            // crashed this whole panel with no error boundary to catch it.
            drone.altitude_msl == null
              ? ['Altitude MSL', 'not reported', 'text-muted-foreground']
              : ['Altitude MSL', `${Math.round(drone.altitude_msl)} m`],
            // Ground speed and heading are NOT here: they are the GND SPEED
            // tile and its sub-line, in the head that never scrolls. Opening
            // this group by default (D52) put the two surfaces on screen at the
            // same time and made that a live duplication rather than a latent
            // one, so the group gave them up to the grid (D56). Vertical speed
            // stays — the tile has one sub-line and heading has it.
            drone.vertical_speed == null
              ? null
              : ['Vertical speed', `${drone.vertical_speed >= 0 ? '+' : ''}${drone.vertical_speed.toFixed(1)} m/s`],
            drone.roll_deg === undefined || drone.pitch_deg === undefined
              ? null
              : ['Attitude', `R ${signed(drone.roll_deg)}° P ${signed(drone.pitch_deg)}°`],
            // Moved off the fact grid (D50). Distance without a bearing is half
            // an answer, and the tile only had one sub-line to spend on it.
            homeM === null
              ? ['Home', 'no home reference', 'text-muted-foreground']
              : ['Home', formatOffsetM(homeM)],
            homeBearing === null
              ? null
              : ['Bearing to home', `${String(homeBearing).padStart(3, '0')}°`],
          ]}
        />
      </Group>

      <Group
        label="Power"
        defaultOpen
        summary={
          drone.battery_voltage === undefined
            ? battery === null ? 'not reported' : `${battery} %`
            : `${drone.battery_voltage.toFixed(1)} V`
        }
        summaryClass={batteryClass}
      >
        <Facts
          rows={[
            battery === null
              ? ['Battery', 'not reported', 'text-muted-foreground']
              : ['Battery', `${battery} %`, batteryClass],
            drone.battery_voltage === undefined
              ? null
              : ['Voltage', `${drone.battery_voltage.toFixed(1)} V`],
            drone.battery_current_a === undefined
              ? null
              : ['Current', `${drone.battery_current_a.toFixed(1)} A`],
            drone.battery_consumed_mah === undefined
              ? null
              : ['Consumed', `${Math.round(drone.battery_consumed_mah)} mAh`],
            drone.battery_temperature === undefined
              ? null
              : ['Cell temperature', `${drone.battery_temperature.toFixed(1)} °C`],
          ]}
        />
      </Group>

      <Group
        label="Link and source"
        summary={linkSummary(drone, link, freshness)}
        summaryClass={linkSummaryClass}
      >
        <Facts
          rows={[
            ['Telemetry source', SOURCE_LABEL[drone.telemetry_source ?? ''] ?? 'Unknown'],
            link.percent === null
              ? ['Signal', link.unavailableReason ?? 'not reported', 'text-muted-foreground']
              : ['Signal', `${link.percent} % · ${SIGNAL_WORD[signalLevel(link.percent)]}`, linkClass],
            drone.comm_drop_rate === undefined
              ? null
              : ['Packet loss', `${drone.comm_drop_rate.toFixed(1)} %`],
            drone.comm_errors === undefined ? null : ['Comm errors', String(drone.comm_errors)],
            ['Satellites', String(drone.satellites ?? 0)],
            drone.gps_vdop === undefined ? null : ['VDOP', drone.gps_vdop.toFixed(1)],
            ['Freshness', freshness.toUpperCase(), FRESHNESS_CLASS[freshness]],
            ['Last packet', formatAge(drone.last_seen_at, now)],
          ]}
        />
      </Group>

      {/* The summary is PROGRESS, not the waypoint count (D59). The count is the
          one mission reading that does not change during a sortie, and after
          D49 cut the MISSION tile this summary is the only thing on screen that
          can answer "which leg is it on" without a click. The count is still
          recoverable from the string, and the list under it is the full answer;
          the reverse was not true. */}
      <Group
        label="Mission"
        summary={
          waypoints.length === 0
            ? missionsUnavailable ? 'unavailable' : 'none assigned'
            : waypointIndex === null ? `— / ${waypoints.length} WP`
            : `${waypointIndex} / ${waypoints.length} WP`
        }
        summaryClass={waypoints.length === 0 ? undefined : 'text-status-cyan-fg'}
      >
        <MissionFacts
          mission={mission}
          waypointIndex={waypointIndex}
          missionsUnavailable={missionsUnavailable}
        />
      </Group>

      {/* Below the record: the two blocks that answer questions about the
          aircraft rather than reporting its telemetry. Both are already-loaded
          data, so opening one costs nothing. */}
      <AlertsBlock
        droneId={drone.drone_id}
        alerts={alerts}
        now={now}
        onAcknowledge={onAcknowledgeAlert}
        onResolve={onResolveAlert}
      />
      <MaintenanceBlock predictions={maintenance} />
    </>
  );
}

/**
 * This aircraft's unresolved alarms, with the two verbs that resolve them.
 *
 * Not a second alarm panel: CopAlertBell is the org-wide queue and the state card
 * shows these as one-line exceptions. What neither gives is the alarm's own
 * record — when it fired, what it said — next to the aircraft it fired on, with
 * acknowledge and resolve to hand. Nothing is fetched: useCopAlerts already
 * holds the list, so this block is a filter.
 */
function AlertsBlock({
  droneId,
  alerts,
  now,
  onAcknowledge,
  onResolve,
}: {
  droneId: string;
  alerts: AlarmEntry[];
  now: number;
  onAcknowledge?: (id: string) => void;
  onResolve?: (id: string) => void;
}) {
  const mine = alerts.filter((a) => a.drone_id === droneId && a.status !== 'resolved');
  const worst = mine.some((a) => a.severity === 'critical')
    ? 'text-status-red-fg'
    : mine.length > 0
      ? 'text-status-amber-fg'
      : undefined;

  return (
    <Group label="Alerts" summary={mine.length === 0 ? 'none' : String(mine.length)} summaryClass={worst}>
      {mine.length === 0 ? (
        <p className="rounded-md border border-divider bg-elevated px-2.5 py-2 text-[11px] text-muted-foreground">
          No unresolved alerts for this aircraft.
        </p>
      ) : (
        <ul className="flex flex-col gap-1">
          {mine.map((a) => (
            <li
              key={a.id}
              className={cn(
                'rounded-md border border-l-2 bg-elevated px-2.5 py-1.5',
                a.severity === 'critical'
                  ? 'border-divider border-l-status-red'
                  : 'border-divider border-l-status-amber',
              )}
            >
              <div className="flex items-baseline gap-2">
                <span
                  className={cn(
                    'truncate text-[11px] tracking-[0.05em]',
                    a.severity === 'critical' ? 'text-status-red-fg' : 'text-status-amber-fg',
                  )}
                >
                  {a.title.toUpperCase()}
                </span>
                <span className="ml-auto shrink-0 font-mono tabular-nums text-[10px] text-muted-foreground">
                  {formatAge(a.created_at, now)}
                </span>
              </div>
              {a.message && (
                <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{a.message}</p>
              )}
              {(onAcknowledge || onResolve) && (
                <div className="mt-1.5 flex gap-1.5">
                  {/* An acknowledged alarm keeps its resolve verb and loses the
                      one it has already answered — ISA-18.2's two states, not a
                      single dismiss button. */}
                  {/* The same two words the top row's bell uses on the same two
                      states, because both are reachable at the same moment on
                      the same alert. `ACK` and `RESOLVE` are both retired in
                      `lib/copy/terms.ts`. */}
                  {onAcknowledge && a.status === 'active' && (
                    <AlertVerb onClick={() => onAcknowledge(a.id)}>Acknowledge</AlertVerb>
                  )}
                  {onResolve && <AlertVerb onClick={() => onResolve(a.id)}>Clear</AlertVerb>}
                  {a.status === 'acknowledged' && (
                    <span className="self-center text-[9.5px] uppercase tracking-[0.08em] text-muted-foreground">
                      acknowledged
                    </span>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </Group>
  );
}

function AlertVerb({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded border border-divider px-2 py-0.5 text-[9.5px] uppercase tracking-[0.08em] text-muted-foreground hover:bg-accent hover:text-foreground"
    >
      {children}
    </button>
  );
}

const PREDICTION_CLASS: Record<string, string> = {
  overdue: 'text-status-red-fg',
  due_soon: 'text-status-amber-fg',
  ok: 'text-status-green-fg',
};

const PREDICTION_LABEL: Record<string, string> = {
  overdue: 'OVERDUE',
  due_soon: 'DUE SOON',
  ok: 'OK',
};

/**
 * Whether this airframe is due for service, from the predictions the COP
 * already loads for its header KPI.
 *
 * It belongs beside the aircraft rather than only on the compliance page
 * because the moment it matters is the moment someone is about to launch this
 * drone, and that decision is made here.
 */
function MaintenanceBlock({ predictions }: { predictions: MaintenancePrediction[] }) {
  const worst = predictions.find((p) => p.status === 'overdue')
    ?? predictions.find((p) => p.status === 'due_soon');
  const summary = predictions.length === 0 ? 'no data' : PREDICTION_LABEL[worst?.status ?? 'ok'];

  return (
    <Group
      label="Maintenance"
      summary={summary}
      summaryClass={predictions.length === 0 ? undefined : PREDICTION_CLASS[worst?.status ?? 'ok']}
    >
      {predictions.length === 0 ? (
        <p className="rounded-md border border-divider bg-elevated px-2.5 py-2 text-[11px] text-muted-foreground">
          No maintenance schedule for this airframe.
        </p>
      ) : (
        <Facts
          rows={predictions.map((p): Row => [
            maintenanceTypeLabel(p.type),
            p.hours_until_due < 0
              ? `${Math.abs(p.hours_until_due).toFixed(1)} h over`
              : `${p.hours_until_due.toFixed(1)} h left`,
            PREDICTION_CLASS[p.status],
          ])}
        />
      )}
    </Group>
  );
}

/** Always signed, so a level attitude reads as 0 and not as a missing sign. */
function signed(deg: number): string {
  return `${deg >= 0 ? '+' : ''}${deg.toFixed(0)}`;
}

/**
 * What a closed Link group is allowed to claim. Packet loss is the honest
 * measure for a MAVLink aircraft, which reports no RSSI at all; anything else
 * falls back to the signal percentage, and a feed that has gone quiet says so
 * before either.
 */
/**
 * What a closed Link group says, and the ONLY link reading on screen for most of
 * a sortie now that D49 cut the LINK tile.
 *
 * The percentage carries the band word with it (D58). Without it, "45 %" tinted
 * amber is a marginal link communicated by colour alone - and the amber is not
 * backed by anything else: `exceptionsFor` raises a link exception for staleness
 * only, so a weak-but-fresh signal reaches neither the verdict strip nor the
 * chip's pill. That is the failure mode `DroneSelectionChip` refuses at length
 * for its state word, and it must not be reintroduced one surface over.
 *
 * MAVLink is unaffected either way: it is the only source that sets
 * `comm_drop_rate`, and it reports no percentage at all, so the loss figure
 * never displaces a band word that would otherwise have been shown.
 */
function linkSummary(
  drone: LiveDrone,
  link: { percent: number | null },
  freshness: ReturnType<typeof linkFreshness>,
): string {
  if (freshness !== 'live') return freshness.toUpperCase();
  if (drone.comm_drop_rate !== undefined) return `${drone.comm_drop_rate.toFixed(1)} % loss`;
  if (link.percent !== null) return `${link.percent} % · ${SIGNAL_WORD[signalLevel(link.percent)]}`;
  return 'not reported';
}

function MissionFacts({
  mission,
  waypointIndex,
  missionsUnavailable = false,
}: {
  mission?: ActiveMission | null;
  waypointIndex: number | null;
  missionsUnavailable?: boolean;
}) {
  const waypoints = mission?.waypoints ?? [];

  // "None assigned" is an assertion about the aircraft, and it may only be made
  // when the mission set was actually read. If that read failed, the truthful
  // statement is about our own ignorance — an operator who has been shown this
  // panel during a live sortie must never be told there is no mission when the
  // real answer is that nobody could ask.
  if (!mission || waypoints.length === 0) {
    return (
      <p className="rounded-md border border-divider bg-elevated px-2.5 py-2 text-[11px] text-muted-foreground">
        {missionsUnavailable
          ? 'Mission data unavailable — retrying.'
          : 'No mission assigned to this aircraft.'}
      </p>
    );
  }

  return (
    <>
      {/* Status and progress were the MISSION tile's two lines until D49 cut it.
          They land here rather than being deleted with it: the waypoint list
          below marks the INBOUND leg, but nothing else in the panel says which
          number that is out of how many, or whether the mission is running.

          A mission with no reported progress shows its size and not a position
          in it: "— / 6" cannot be misread as "sitting at the start", which is
          what "0 / 6" says about an aircraft that is in fact flying the route. */}
      <Facts
        rows={[
          ['Name', mission.name],
          // Through the copy module's closed set, not by un-underscoring the
          // wire value: `terms.ts` rule 5 is "never render a wire enum", and
          // mechanically tidying `in_progress` is still rendering it - it just
          // reads acceptably for the four statuses that exist today (D64).
          ['Status', STATUS_LABEL[mission.status as keyof typeof STATUS_LABEL] ?? 'Assigned'],
          waypointIndex === null
            ? ['Progress', `— / ${waypoints.length} · progress not reported`, 'text-muted-foreground']
            : ['Progress', `${waypointIndex} / ${waypoints.length}`],
        ]}
      />

      <Section label="Waypoints">
        <ol className="overflow-hidden rounded-md border border-divider">
          {waypoints.map((wp, i) => {
            const n = i + 1;
            // PASSED/INBOUND/QUEUED are claims about where the aircraft is. With
            // no progress feed there is nothing to base them on, so every leg
            // reads PLANNED — the route is known, the aircraft's place in it
            // is not.
            const state = waypointIndex === null ? 'PLANNED'
              : n < waypointIndex ? 'PASSED'
              : n === waypointIndex ? 'INBOUND'
              : 'QUEUED';
            return (
              <li
                key={n}
                className={cn(
                  'grid grid-cols-[26px_1fr_auto] items-center gap-2 border-b border-divider bg-elevated px-2 py-1.5 text-[11px] last:border-b-0',
                  state === 'PASSED' && 'text-muted-foreground',
                  state === 'INBOUND' && 'bg-status-cyan/10 text-foreground',
                )}
              >
                <span className="font-mono tabular-nums text-muted-foreground">{String(n).padStart(2, '0')}</span>
                <span className="font-mono tabular-nums">{`${wp.latitude.toFixed(4)}, ${wp.longitude.toFixed(4)}`}</span>
                <span className={cn('text-[10px]', state === 'INBOUND' && 'text-status-cyan-fg')}>
                  {state}
                </span>
              </li>
            );
          })}
        </ol>
      </Section>
    </>
  );
}

/** What is actually fitted to the airframe, read from the registry rather than
 *  inferred from the stream — the picture can be down and the sensor still be
 *  bolted on, and the operator needs to know which of those they are looking at. */
const IMAGING_PAYLOADS = new Set(['camera', 'thermal']);

/** Green under 300 ms, amber under 800, red above. */
function latencyClass(ms: number): string {
  if (ms < 300) return 'text-status-green-fg';
  if (ms < 800) return 'text-status-amber-fg';
  return 'text-status-red-fg';
}

/**
 * What each camera IS, where its picture is, and the one key that acts on it.
 *
 * No picture. This panel used to be video's home — every registered camera
 * played here, directly above the rows describing it, with camera control on
 * the tile. Picking an aircraft on the map now opens its picture in a window
 * over the map, so video has one home and this panel keeps what a picture
 * cannot tell you: protocol, resolution, the registered latency, how stale the
 * registry row is, and which camera is fitted.
 *
 * Camera control went with the picture, reversing the decision recorded here
 * before: the overlay was moved INTO this panel because an operator who never
 * popped a camera out never found a gimbal control, FEED's only affordance
 * being a POP OUT key that did not say what popping out got you. The map click
 * answers that discoverability problem better — the picture and its controls
 * now arrive together, unasked — and it removes the reason the compromise
 * existed. Aiming a gimbal from a surface that is not showing what it sees is
 * worse than a control that is hard to find.
 *
 * Verb-free where PLACEMENT is concerned: open a window, bring it to the front,
 * return it to the stage. Never a close — a panel must not be able to end a
 * picture the operator is watching somewhere else. Recording state, when the
 * backend can answer it, arrives as a labelled readout and never as a toggle.
 */
function FeedSegment({
  drone,
  registryRow,
  streams,
  poppedCameras = [],
  loading,
  error,
  onRetry,
  onPopOutVideo,
  onDockVideo,
  stagePlacement = null,
  now,
}: {
  /** For the camera-pose rows — the live track, not the registry row: pointing
   *  is telemetry, and it changes during a sortie. */
  drone: LiveDrone;
  registryRow?: Drone | null;
  streams: CopVideoStream[];
  poppedCameras: string[];
  stagePlacement?: ManualStageLayout | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onPopOutVideo: (cameraId?: string) => void;
  onDockVideo: (cameraId?: string) => void;
  now: number;
}) {
  // An airframe carries more than one camera. Each gets its own health block and
  // its own placement key, so the operator opens over the map the ones they
  // want to watch and reads the numbers for the rest here.
  const hasAny = streams.length > 0;
  // The camera the stage is drawing, if it is drawing one: the same choice the
  // stage made, from the same facts, so this panel never draws it a second time.
  const onStage = stagePlacement ? chooseStream(streams, poppedCameras).stream?.id ?? null : null;

  /**
   * The key to ACT on for one camera: not always the camera's own.
   *
   * The window a map click opens names no camera — it carries SINGLE_CAMERA,
   * the aircraft's default — so on a two-camera airframe "open the nose camera"
   * has to reach THAT window rather than open a second one on the same stream.
   * Only when this camera really is the one that window is drawing; every other
   * camera acts on its own name as before.
   */
  const windowKeyFor = (s: CopVideoStream): string | undefined =>
    poppedCameras.includes(SINGLE_CAMERA)
    && (s.camera_id ?? SINGLE_CAMERA) === resolveCameraKey(streams, SINGLE_CAMERA)
      ? SINGLE_CAMERA
      : s.camera_id ?? undefined;

  return (
    <>
      {hasAny ? (
        streams.map((s) => (
          <Section
            key={s.id}
            label={streams.length > 1 ? `${s.label ?? s.camera_id ?? 'Camera'} camera` : 'Stream health'}
          >
            <StreamHealth
              stream={s}
              isPopped={isCameraPopped(streams, poppedCameras, s.camera_id ?? SINGLE_CAMERA)}
              drawnOnStage={s.id === onStage ? stagePlacement : null}
              returnsToStage={
                stagePlacement !== null
                && stageTakesBack(streams, poppedCameras, s.camera_id ?? SINGLE_CAMERA)
              }
              loading={false}
              error={null}
              onRetry={onRetry}
              onPopOutVideo={() => onPopOutVideo(windowKeyFor(s))}
              onDockVideo={() => onDockVideo(windowKeyFor(s))}
              now={now}
            />
          </Section>
        ))
      ) : (
        <Section label="Stream health">
          <StreamHealth
            stream={null}
            isPopped={false}
            loading={loading}
            error={error}
            onRetry={onRetry}
            onPopOutVideo={() => onPopOutVideo()}
            onDockVideo={() => onDockVideo()}
            now={now}
          />
        </Section>
      )}
      <CameraPoseFacts drone={drone} />
      <SensorFacts registryRow={registryRow} />
    </>
  );
}

/**
 * The numbers that describe one camera, and the one key that acts on its
 * picture.
 *
 * This panel draws no video. It used to: the picture sat here above its own
 * health rows, with a pop-out key to send it over the map. Clicking an aircraft
 * on the map now opens that window directly, so video has exactly one home and
 * the panel keeps what a picture cannot tell you — the protocol, the
 * resolution, the registered latency, and how long ago the registry heard from
 * the publisher.
 *
 * Camera control went with the picture. Its bar used to render here because
 * this panel is the one surface that cannot drag; a gimbal commanded from a
 * surface that is not SHOWING what it sees is worse than one that is awkward to
 * aim, so the keys live on the window and the stage, which both draw the tile
 * they slew.
 */
function StreamHealth({
  stream,
  isPopped,
  drawnOnStage = null,
  returnsToStage = false,
  loading,
  error,
  onRetry,
  onPopOutVideo,
  onDockVideo,
  now,
}: {
  stream: CopVideoStream | null;
  /** This camera is playing in its own floating window over the map. */
  isPopped: boolean;
  /** This camera is playing on the stage (a manual session or the regular-mode
   *  split), and where the stage put it. Opening its own window still works;
   *  the stage yields to the window. */
  drawnOnStage?: ManualStageLayout | null;
  /** Closing this camera's window hands it back to the stage — the only
   *  destination left, so the verb is offered only when that is true. */
  returnsToStage?: boolean;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onPopOutVideo: () => void;
  onDockVideo: () => void;
  now: number;
}) {
  // "I could not ask" and "there is nothing to ask about" are different facts,
  // and only one of them means the camera is off. They get different screens.
  // A failed refresh over a stream we already read keeps the rows instead —
  // last known is still last known, and the red dot carries the doubt.
  if (error && !stream) {
    return (
      <>
        <Note>
          Could not read stream health. The aircraft may still be broadcasting — this is a failure
          to reach the video registry, not proof the feed is down.
        </Note>
        <Handoff onClick={onRetry}>RETRY</Handoff>
      </>
    );
  }

  if (!stream) {
    return loading ? (
      <>
        {/* The skeleton holds the rows' shape so the segment does not jump when
            the first answer lands. */}
        <Facts
          rows={[
            ['Status', 'reading…', 'text-muted-foreground'],
            ['Protocol', '—', 'text-muted-foreground'],
            ['Resolution', '—', 'text-muted-foreground'],
            ['Latency', '—', 'text-muted-foreground'],
            ['Last updated', '—', 'text-muted-foreground'],
          ]}
        />
        <Handoff disabled reason="Waiting for the stream registry">
          OPEN VIDEO WINDOW <span aria-hidden>↗</span>
        </Handoff>
      </>
    ) : (
      <>
        <Note>No stream registered for this aircraft.</Note>
        <Handoff disabled reason="No stream is registered for this aircraft">
          NO STREAM TO SHOW
        </Handoff>
      </>
    );
  }

  const status = (stream.status || '').toUpperCase() || 'UNKNOWN';
  const latency = stream.latency_ms;

  return (
    <>
      {/* Where this camera's picture is, and the one key that acts on it. Never
          a picture: this panel says where video IS, the window and the stage
          are where video plays. */}
      {isPopped ? (
        <>
          {/* "Open in", not "playing in": this panel knows a window exists for
              this camera, not that a picture is arriving in it. The window may
              be showing an error or a dead player, and the health dot beside
              this tab is what answers that. */}
          <Note>Open in its own window over the map.</Note>
          {/* Two verbs, because a window has two different exits. With a stage
              waiting, closing the window MOVES the picture there and the key
              says so. With no stage there is nowhere to move it to, so the only
              thing worth offering is "bring that window to the front" — which
              is what pressing an already-open camera does. Never a close key:
              this panel must not be able to end a picture the operator is
              watching somewhere else. */}
          {returnsToStage ? (
            <Handoff onClick={onDockVideo}>
              RETURN TO STAGE <span aria-hidden>⤓</span>
            </Handoff>
          ) : (
            <Handoff onClick={onPopOutVideo}>
              BRING WINDOW TO FRONT <span aria-hidden>↗</span>
            </Handoff>
          )}
        </>
      ) : drawnOnStage ? (
        <>
          <Note>{stagePlacementSentence(drawnOnStage)}</Note>
          <Handoff onClick={onPopOutVideo}>
            OPEN VIDEO WINDOW <span aria-hidden>↗</span>
          </Handoff>
        </>
      ) : (
        <>
          {/* "Opens in", not "plays in": no window is open for this camera, so
              the present tense would assert a picture that is not on screen
              anywhere. This sentence says where video LIVES — it is the one
              thing an operator who has never used this panel needs told.

              A degraded feed is still worth looking at — that is the operator's
              call, not ours — so the key is offered whatever the status says. */}
          <Note>Video opens in its own window over the map.</Note>
          <Handoff onClick={onPopOutVideo}>
            OPEN VIDEO WINDOW <span aria-hidden>↗</span>
          </Handoff>
        </>
      )}
      <Facts
        rows={[
          ['Status', status, isHealthy(stream) ? 'text-status-green-fg' : 'text-status-amber-fg'],
          ['Protocol', stream.type.toUpperCase()],
          stream.resolution
            ? ['Resolution', stream.resolution]
            : ['Resolution', 'not reported', 'text-muted-foreground'],
          latency == null
            ? ['Latency', 'not reported', 'text-muted-foreground']
            : ['Latency', `${latency} ms`, latencyClass(latency)],
          stream.updated_at
            ? ['Last updated', formatAge(stream.updated_at, now)]
            : ['Last updated', 'not reported', 'text-muted-foreground'],
        ]}
      />
    </>
  );
}

/**
 * Where the camera is POINTED, and how far it is zoomed in.
 *
 * Observation, never command. These four readings used to reach the operator
 * only through `CameraControlOverlay`, which renders them for every role
 * including a `viewer` who can command nothing — and the overlay went with the
 * picture when video moved out to a floating window. That left an operator with
 * no window open, or no permission to open a control, unable to answer "where
 * is that camera looking" from this panel at all, which it could before.
 *
 * So they come back here as rows, with no keys attached. `—` for anything the
 * aircraft does not report: a lens in particular is never assumed to be wide,
 * because "we have not been told" and "it is wide" are different facts and only
 * one of them means the operator is looking at the wide picture.
 */
function CameraPoseFacts({ drone }: { drone: LiveDrone }) {
  const pitch = formatPitchReadout(drone.gimbal_pitch);
  const yaw = formatYawReadout(drone.gimbal_yaw);
  const zoom = formatZoomReadout(drone.zoom_ratio);
  const lens = formatLensReadout(drone.camera_lens);
  const dim = 'text-muted-foreground';
  return (
    <Section label="Camera pose">
      <Facts
        rows={[
          ['Gimbal pitch', pitch, pitch === '—' ? dim : undefined],
          ['Gimbal yaw', yaw, yaw === '—' ? dim : undefined],
          ['Zoom', zoom, zoom === '—' ? dim : undefined],
          ['Lens', lens, lens === '—' ? dim : undefined],
        ]}
      />
    </Section>
  );
}

function SensorFacts({ registryRow }: { registryRow?: Drone | null }) {
  // With no registry row we cannot say what is fitted, and guessing would be
  // worse than saying nothing - but saying NOTHING is now worse than both.
  //
  // This used to return null on the grounds that "A/C already reports that
  // gap", and A/C is deleted (D53). `registryRow` comes from the REST fleet
  // fetch while the aircraft comes from the WebSocket, so the two genuinely
  // disagree - a failed `/api/drones`, or an aircraft not in the fetched list.
  // Rendering nothing is byte-identical to a normal feed with no camera
  // recorded, so the operator could not tell "nothing fitted" from "we could
  // not read the registry", and the fleet table is no fallback: if
  // `/api/drones` is what failed, the row the expansion hangs off is not there
  // either (D62).
  if (!registryRow) {
    return (
      <Section label="Sensor">
        <Note>Registry unavailable — telemetry only.</Note>
      </Section>
    );
  }

  const camera = (registryRow.payloads ?? []).find((p) => IMAGING_PAYLOADS.has(p.type));

  return (
    <Section label="Sensor">
      <Facts
        rows={[
          camera ? ['Payload', camera.name] : ['Payload', 'None recorded', 'text-muted-foreground'],
        ]}
      />
    </Section>
  );
}

function Note({ children }: { children: React.ReactNode }) {
  return (
    <p className="rounded-md border border-divider bg-elevated px-2.5 py-2 text-[11px] text-muted-foreground">
      {children}
    </p>
  );
}

/**
 * The one door from the data to the picture. Disabled states carry the reason
 * rather than vanishing: a button that disappears teaches nothing, and the
 * operator is owed the difference between "nothing to open" and "not yet known".
 */
function Handoff({
  children,
  onClick,
  disabled = false,
  reason,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  reason?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={disabled ? reason : undefined}
      className={cn(
        'mt-2 w-full rounded-md border py-2 text-center text-[10px] tracking-[0.1em]',
        disabled
          ? 'cursor-not-allowed border-divider bg-elevated text-muted-foreground'
          : 'border-status-blue/40 bg-status-blue/10 text-status-blue-fg hover:bg-status-blue/20',
      )}
    >
      {children}
      {disabled && reason && <span className="sr-only"> — {reason}</span>}
    </button>
  );
}
